import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../Shared/axiosInstance.js";

export default function OpeningBalanceForm() {
  const API_BASE = "/InventoryTransactions";
  const PRODUCTS_API = "/Product";
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [loadingProducts, setLoadingProducts] = useState(false);

  // حالة الـ Modal
  const [showModal, setShowModal] = useState(false);
  const [currentItemIndex, setCurrentItemIndex] = useState(null);

  // جلب المنتجات عند فتح الـ modal أو تغيير البحث
  useEffect(() => {
    if (!showModal) return;

    const fetchProducts = async () => {
      try {
        setLoadingProducts(true);
        const { data } = await axiosInstance.get(PRODUCTS_API, {
          params: {
            name: productSearch || undefined,
            pageNumber: 1,
            pageSize: 15,
          },
        });
        setProducts(data.data || []);
      } catch (err) {
        console.error("Error fetching products:", err);
        Swal.fire("خطأ", "فشل في تحميل المنتجات", "error");
      } finally {
        setLoadingProducts(false);
      }
    };

    const delay = setTimeout(fetchProducts, 300);
    return () => clearTimeout(delay);
  }, [productSearch, showModal]);

  const formik = useFormik({
    initialValues: {
      items: [{ productId: "", productName: "", quantity: 0 }],
    },
    validationSchema: Yup.object({
      items: Yup.array().of(
        Yup.object({
          productId: Yup.string().required("المنتج مطلوب"),
          quantity: Yup.number()
            .required("الكمية مطلوبة")
            .min(1, "يجب أن تكون الكمية 1 على الأقل"),
        })
      ),
    }),
    onSubmit: async (values) => {
      try {
        const payload = {
          items: values.items.map((item) => ({
            productId: item.productId,
            quantity: parseInt(item.quantity) || 0,
          })),
        };

        console.log("Sending payload:", payload);

        const { data } = await axiosInstance.post(
          `${API_BASE}/opening-balance`,
          payload
        );

        await Swal.fire({
          title: "تم بنجاح!",
          text: data.message || "تم إضافة الرصيد الافتتاحي بنجاح",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });

        navigate("/products");
        formik.resetForm();
      } catch (err) {
        console.error("Error:", err);
        Swal.fire(
          "خطأ",
          err.response?.data?.message || "حدث خطأ، الرجاء المحاولة مرة أخرى",
          "error"
        );
      }
    },
  });

  const addNewItem = () => {
    formik.setFieldValue("items", [
      ...formik.values.items,
      { productId: "", productName: "", quantity: 0 },
    ]);
  };

  const removeItem = (index) => {
    const newItems = formik.values.items.filter((_, i) => i !== index);
    formik.setFieldValue(
      "items",
      newItems.length ? newItems : [{ productId: "", productName: "", quantity: 0 }]
    );
  };

  const openProductModal = (index) => {
    setCurrentItemIndex(index);
    setShowModal(true);
    setProductSearch(""); // إعادة تعيين البحث
  };

  const selectProduct = (product) => {
    if (currentItemIndex === null) return;

    formik.setFieldValue(`items[${currentItemIndex}].productId`, product.id);
    formik.setFieldValue(
      `items[${currentItemIndex}].productName`,
      `${product.name} - ${product.description}`
    );

    setShowModal(false);
    setCurrentItemIndex(null);
  };

  return (
    <div style={{ background: "#f8fafc", minHeight: "100vh", paddingBottom: "40px" }}>
      <div className="container py-5" style={{ maxWidth: "1000px" }}>
        
        {/* الرأس */}
        <div className="d-flex justify-content-between align-items-center mb-4 mt-5 pt-5">
          <div>
            <h2 style={{ color: "#2563eb", fontWeight: "700", fontSize: "28px", marginBottom: "4px" }}>
              <i className="bi bi-clipboard-data me-2"></i>إضافة رصيد افتتاحي
            </h2>
            <small style={{ color: "#64748b", fontSize: "14px" }}>
              تعيين الكميات الافتتاحية للمخزون للمنتجات
            </small>
          </div>
          <button
            type="button"
            onClick={() => navigate("/products")}
            style={{
              background: "#64748b",
              color: "#fff",
              padding: "10px 20px",
              borderRadius: "6px",
              border: "none",
              fontWeight: "600",
              fontSize: "15px",
              cursor: "pointer",
            }}
          >
            <i className="bi bi-arrow-left me-1"></i> عودة
          </button>
        </div>

        <form onSubmit={formik.handleSubmit}>
          
          {/* قسم المنتجات */}
          <div style={cardStyle}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 style={sectionTitleStyle}>
                <i className="bi bi-box-seam me-2"></i>المنتجات ({formik.values.items.length})
              </h5>
              <button
                type="button"
                onClick={addNewItem}
                style={{
                  background: "#2563eb",
                  color: "#fff",
                  padding: "8px 16px",
                  borderRadius: "6px",
                  border: "none",
                  fontWeight: "600",
                  fontSize: "14px",
                  cursor: "pointer",
                }}
              >
                <i className="bi bi-plus-circle me-1"></i> إضافة منتج
              </button>
            </div>

            {formik.values.items.map((item, index) => (
              <div key={index} style={itemCardStyle}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span style={{ color: "#2563eb", fontWeight: "600", fontSize: "15px" }}>
                    المنتج رقم {index + 1}
                  </span>
                  {formik.values.items.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      style={{
                        background: "#dc2626",
                        color: "#fff",
                        padding: "4px 12px",
                        borderRadius: "4px",
                        border: "none",
                        fontSize: "13px",
                        fontWeight: "600",
                        cursor: "pointer",
                      }}
                    >
                      <i className="bi bi-trash me-1"></i> إزالة
                    </button>
                  )}
                </div>

                <div className="mb-3">
                  <label style={labelStyle}>المنتج</label>
                  <div className="d-flex gap-2">
                    <input
                      type="text"
                      value={item.productName || "لم يتم اختيار منتج"}
                      readOnly
                      style={{ ...inputStyle, flex: 1, background: "#f8fafc", cursor: "default" }}
                    />
                    <button
                      type="button"
                      onClick={() => openProductModal(index)}
                      style={{
                        background: "#2563eb",
                        color: "#fff",
                        padding: "10px 20px",
                        borderRadius: "6px",
                        border: "none",
                        fontWeight: "600",
                        fontSize: "14px",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <i className="bi bi-search me-1"></i> اختيار
                    </button>
                  </div>
                  {formik.touched.items?.[index]?.productId &&
                    formik.errors.items?.[index]?.productId && (
                      <div style={errorStyle}>{formik.errors.items[index].productId}</div>
                    )}
                </div>

                <div className="row g-3">
                  <div className="col-md-12">
                    <label style={labelStyle}>الكمية</label>
                    <input
                      name={`items[${index}].quantity`}
                      type="number"
                      min="0"
                      placeholder="أدخل الكمية"
                      value={item.quantity}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      onWheel={(e) => e.target.blur()}
                      style={inputStyle}
                    />
                    {formik.touched.items?.[index]?.quantity &&
                      formik.errors.items?.[index]?.quantity && (
                        <div style={errorStyle}>{formik.errors.items[index].quantity}</div>
                      )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* زر الحفظ */}
          <button
            type="submit"
            disabled={!(formik.dirty && formik.isValid)}
            style={{
              width: "100%",
              background: formik.dirty && formik.isValid 
                ? "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)" 
                : "#94a3b8",
              color: "#fff",
              padding: "16px",
              borderRadius: "8px",
              border: "none",
              fontWeight: "700",
              fontSize: "18px",
              cursor: formik.dirty && formik.isValid ? "pointer" : "not-allowed",
              boxShadow: formik.dirty && formik.isValid 
                ? "0 4px 12px rgba(34, 197, 94, 0.3)" 
                : "none",
              transition: "all 0.3s ease",
            }}
          >
            <i className="bi bi-check-circle me-2"></i> حفظ الرصيد الافتتاحي
          </button>
        </form>
      </div>

      {/* 🎯 نافذة اختيار المنتج */}
      {showModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "12px",
              width: "90%",
              maxWidth: "700px",
              maxHeight: "80vh",
              overflow: "hidden",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* رأس النافذة */}
            <div style={{
              padding: "20px 24px",
              borderBottom: "1px solid #e2e8f0",
              background: "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)",
            }}>
              <div className="d-flex justify-content-between align-items-center">
                <h5 style={{ color: "#fff", fontWeight: "700", fontSize: "20px", margin: 0 }}>
                  <i className="bi bi-search me-2"></i>اختيار منتج
                </h5>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{
                    background: "rgba(255, 255, 255, 0.2)",
                    color: "#fff",
                    border: "none",
                    borderRadius: "50%",
                    width: "32px",
                    height: "32px",
                    cursor: "pointer",
                    fontSize: "20px",
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            {/* محتوى النافذة */}
            <div style={{ padding: "20px 24px" }}>
              <input
                type="text"
                placeholder="🔍 ابحث عن المنتجات بالاسم..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                autoFocus
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "2px solid #bfdbfe",
                  borderRadius: "8px",
                  fontSize: "15px",
                  marginBottom: "16px",
                  outline: "none",
                }}
              />

              <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                {loadingProducts ? (
                  <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
                    <div className="spinner-border text-primary" role="status"></div>
                    <p style={{ marginTop: "12px" }}>جارٍ تحميل المنتجات...</p>
                  </div>
                ) : products.length > 0 ? (
                  <div>
                    {products.map((product) => (
                      <button
                        key={product.id}
                        type="button"
                        onClick={() => selectProduct(product)}
                        style={{
                          width: "100%",
                          padding: "16px",
                          marginBottom: "8px",
                          border: "1px solid #e2e8f0",
                          borderRadius: "8px",
                          background: "#fff",
                          textAlign: "left",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#f0f9ff";
                          e.currentTarget.style.borderColor = "#2563eb";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "#fff";
                          e.currentTarget.style.borderColor = "#e2e8f0";
                        }}
                      >
                        <div style={{ fontWeight: "600", color: "#1e293b", marginBottom: "4px" }}>
                          {product.name}
                        </div>
                        <div style={{ fontSize: "14px", color: "#64748b" }}>
                          {product.description}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
                    <i className="bi bi-inbox" style={{ fontSize: "48px", marginBottom: "12px" }}></i>
                    <p>لا توجد منتجات</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ✅ الأنماط (لم تتغير)
const cardStyle = {
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: "12px",
  padding: "24px",
  marginBottom: "20px",
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
};

const sectionTitleStyle = {
  color: "#1e293b",
  fontWeight: "700",
  fontSize: "18px",
  marginBottom: "20px",
};

const labelStyle = {
  color: "#475569",
  fontSize: "14px",
  fontWeight: "600",
  marginBottom: "6px",
  display: "block",
};

const inputStyle = {
  width: "100%",
  padding: "10px 14px",
  border: "1px solid #cbd5e1",
  borderRadius: "6px",
  fontSize: "15px",
  color: "#1e293b",
  outline: "none",
  transition: "all 0.2s",
};

const errorStyle = {
  color: "#dc2626",
  fontSize: "13px",
  marginTop: "4px",
  fontWeight: "500",
};

const itemCardStyle = {
  background: "#f8fafc",
  border: "2px solid #e0e7ff",
  borderRadius: "8px",
  padding: "16px",
  marginBottom: "12px",
};
